import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {Alert, Button, Empty, Input, Spin, Table, Tag, Tooltip} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import moment from 'moment';
import ExchangeSelect from 'components/exchangeSelect/ExchangeSelect';
import {useNewsExchange} from '../shared/useNewsExchange';
import {loadError, notDeployed} from '../shared/errors';
import {HowItWorks, Tip} from '../shared/ui';
import WebsitePicker from '../shared/WebsitePicker';
import {useDebounced} from '../shared/useDebounced';
import {exchangeCode} from '../sources/sourceHelpers';
import {GET_NEWS_DRAFTS} from './resolvers';
import {DRAFT, HOW_IT_WORKS, LANGUAGE_LABEL, LIST} from './copy';
import {nameList} from './draftHelpers';
import styles from '../shared/news.module.scss';

// The review operations sit on Website News's sub-tab in news-service.
export const REVIEW_SUB_TAB = 'website-news';

const PAGE_SIZE = 20;

// Drafts waiting for an editor: newest first, or best match when searching.
const NewsReview = () => {
  const navigate = useNavigate();
  const {exchange, exchangeId, selectExchange, waiting} = useNewsExchange();
  const x = exchangeCode(exchange);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [sourceId, setSourceId] = useState();
  const search = useDebounced(searchText.trim()) || undefined;

  useEffect(() => setPage(1), [exchangeId, sourceId, search]);
  useEffect(() => {
    setSearchText('');
    setSourceId(undefined);
  }, [exchangeId]);

  const {data, loading, error} = useQuery(GET_NEWS_DRAFTS, {
    variables: {
      exchangeId,
      status: 'PENDING_REVIEW',
      sourceId,
      search,
      limit: PAGE_SIZE,
      page,
    },
    skip: !exchangeId,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const response = data?.newsDrafts;
  const ok = response?.__typename === 'NewsDraftList';
  const drafts = ok ? response.data.filter(Boolean) : [];
  const total = ok ? response.pagination?.total ?? drafts.length : 0;
  let emptyText = LIST.empty;
  if (search) emptyText = LIST.emptySearch;
  else if (sourceId) emptyText = LIST.emptyWebsite;
  const failure = response?.__typename === 'Error' ? response : error;
  const open = draft => navigate(`/dashboard/news/review/${draft.id}`);

  const columns = [
    {
      title: 'Headline',
      key: 'headline',
      render: (_, draft) => (
        <>
          <Button
            type="link"
            className={styles.linkButton}
            onClick={() => open(draft)}>
            {draft.title}
          </Button>
          {draft.requestedBy || draft.heldBecause?.length ? (
            <p className={styles.reason}>
              {draft.requestedBy
                ? DRAFT.writtenOnRequest
                : DRAFT.heldBecause(nameList(draft.heldBecause))}
            </p>
          ) : null}
        </>
      ),
    },
    {
      title: LIST.from,
      key: 'outlets',
      render: (_, draft) => {
        const outlets = (draft.sources || [])
          .map(source => source.outlet)
          .filter((outlet, i, all) => outlet && all.indexOf(outlet) === i);
        return outlets.length ? outlets.map(o => <Tag key={o}>{o}</Tag>) : '—';
      },
    },
    {
      title: 'Language',
      key: 'language',
      render: (_, draft) => LANGUAGE_LABEL[draft.language] || '—',
    },
    {
      title: 'Written',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => (value ? moment(value).fromNow() : '—'),
    },
    {
      title: '',
      key: 'open',
      render: (_, draft) => (
        <Button onClick={() => open(draft)}>{LIST.open}</Button>
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <span className={styles.tipLabel}>
          <Tip label={LIST.exchange} tip={LIST.exchangeTip} />
          <ExchangeSelect
            className={styles.exchangeSelect}
            value={exchangeId}
            onChange={selectExchange}
            placeholder="Pick an exchange"
          />
        </span>
        {exchangeId && !failure ? (
          <Tip label={LIST.waiting(total)} tip={LIST.waitingTip} />
        ) : null}
      </div>

      {waiting ? (
        <Spin className={styles.spinner} />
      ) : !exchangeId ? (
        <Empty description={LIST.pickExchange} />
      ) : (
        <>
          <HowItWorks id="review">{HOW_IT_WORKS}</HowItWorks>

          <div className={styles.filters}>
            <Tooltip title={LIST.searchTip}>
              <Input
                allowClear
                className={styles.searchBox}
                prefix={<SearchOutlined />}
                placeholder={LIST.search}
                aria-label={LIST.search}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </Tooltip>
            <span className={styles.tipLabel}>
              <Tip label={LIST.website} tip={LIST.websiteTip} />
              <WebsitePicker
                className={styles.exchangeSelect}
                label={LIST.website}
                exchangeId={exchangeId}
                value={sourceId}
                onChange={setSourceId}
              />
            </span>
          </div>
          {failure ? (
            <Alert
              type={notDeployed(failure.message) ? 'info' : 'error'}
              showIcon
              message={
                notDeployed(failure.message)
                  ? LIST.notReady(x)
                  : loadError('the drafts', failure)
              }
            />
          ) : !loading && !drafts.length ? (
            <Empty description={emptyText} />
          ) : (
            <Table
              className="antd-table"
              columns={columns}
              dataSource={drafts}
              loading={loading}
              rowKey="id"
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total,
                showSizeChanger: false,
                hideOnSinglePage: true,
                onChange: setPage,
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default NewsReview;
